import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useDpApiEndpoint } from 'endpoints/dpApi';
import { useRequiredContext } from 'hooks';
import { useReviews } from 'hooks/data-fetching';
import type {
	QuestionSummary,
	Review,
	ReviewScore,
	ReviewSorting,
} from 'models/api';
import type {
	ProductType,
	ReviewScoreDistribution,
	VariantId,
} from 'models/product';
import { ignorePromiseRejection } from 'utils/helpers';

import { type ImageReviewItem, useReviewImages } from '../hooks';

interface ReviewContextType {
	closeReviewsPopover: () => void;
	fit3Summary: QuestionSummary | undefined;
	hasImagesFilter: boolean;
	isLoadingFit: boolean;
	isLoadingInitialReviews: boolean;
	isLoadingMoreReviews: boolean;
	isLoadingReviewImages: boolean;
	isLoadingReviews: boolean;
	isLoadingSelectedReviews: boolean;
	isReviewsPopoverOpen: boolean;
	loadMoreReviews: () => void;
	openReviewsPopover: () => void;
	resetReviewScore: () => void;
	reviewAiSummary: string | undefined;
	reviewImages: ImageReviewItem[];
	reviews: Review[] | undefined;
	reviewsHasNextPage: boolean;
	selectedReviews: Review[] | undefined;
	selectedReviewScore: ReviewScore | undefined;
	selectedReviewsSortOption: ReviewSorting | undefined;
	setHasImagesFilter: (hasImages: boolean) => void;
	setReviewScore: (score: ReviewScore | undefined) => void;
	setReviewsSortOption: (option: ReviewSorting | undefined) => void;
}

export const ReviewContext = createContext<ReviewContextType | undefined>(
	undefined,
);

interface Props {
	children: ReactNode;
	productType: ProductType;
	reviewScore: ReviewScoreDistribution | undefined;
	variantId: VariantId;
}

export function ReviewContextProvider({
	children,
	productType,
	reviewScore,
	variantId,
}: Props) {
	const [isReviewsPopoverOpen, setIsReviewsPopoverOpen] = useState(false);
	const [hasImagesFilter, setHasImagesFilter] = useState(false);

	const [selectedReviewScore, setSelectedReviewScore] = useState<ReviewScore>();
	const [selectedReviewsSortOption, setSelectedReviewsSortOption] =
		useState<ReviewSorting>();

	const openReviewsPopover = useCallback(() => {
		setIsReviewsPopoverOpen(true);
	}, []);
	const closeReviewsPopover = useCallback(() => {
		setIsReviewsPopoverOpen(false);
	}, []);
	const setReviewScore = useCallback((score: ReviewScore | undefined) => {
		setSelectedReviewScore(score);
	}, []);
	const resetReviewScore = useCallback(() => {
		setSelectedReviewScore(undefined);
	}, []);

	const setReviewsSortOption = useCallback(
		(option: ReviewSorting | undefined) => {
			setSelectedReviewsSortOption(option);
		},
		[],
	);

	const isActive = productType === 'Product' && Boolean(reviewScore?.count);
	const {
		hasNextPage: reviewsHasNextPage,
		isLoading: isLoadingReviews,
		isLoadingMore: isLoadingMoreReviews,
		items: reviews,
		loadMore,
	} = useReviews({
		filter: hasImagesFilter ? 'HasImage' : undefined,
		isActive: isActive && isReviewsPopoverOpen,
		reviewScore: selectedReviewScore,
		reviewSorting: selectedReviewsSortOption,
		variantId,
	});

	// These are to be selected reviews, but are not currently
	// since we don't have the option from trustvoice, but
	// name it that way to keep the intent.
	const { data: selectedReviews, isLoading: isLoadingSelectedReviews } =
		useDpApiEndpoint(
			{
				name: 'GetReviews',
				pathParams: { variantId },
				queryParams: {
					reviewSorting: 'Relevance',
					pageSize: 3,
					returnReviewSummary: 'true',
				},
			},
			{ isActive },
		);

	const loadMoreReviews = useCallback(() => {
		ignorePromiseRejection(loadMore());
	}, [loadMore]);

	const { isLoading: isLoadingReviewImages, reviewImages } = useReviewImages({
		variantId,
		isActive,
	});

	const value = useMemo(
		() => ({
			closeReviewsPopover,
			// Use the initial "selected reviews" for this since we need the summary
			// first thing and the selected are the only one we always load and the
			// summary is aggregated and independent of pageSize.
			fit3Summary: selectedReviews?.questionsSummary?.find(
				({ name }) => name === 'fit3',
			),
			hasImagesFilter,
			isLoadingFit: isLoadingSelectedReviews,
			isLoadingReviews,
			isLoadingInitialReviews: isLoadingReviews && !reviews,
			isLoadingMoreReviews: Boolean(isLoadingMoreReviews),
			isLoadingReviewImages,
			isLoadingSelectedReviews,
			isReviewsPopoverOpen,
			loadMoreReviews,
			openReviewsPopover,
			resetReviewScore,
			reviewAiSummary: selectedReviews?.summary,
			reviewImages,
			reviews,
			reviewsHasNextPage,
			selectedReviews: selectedReviews?.reviews,
			selectedReviewScore,
			selectedReviewsSortOption,
			setHasImagesFilter,
			setReviewScore,
			setReviewsSortOption,
		}),
		[
			closeReviewsPopover,
			hasImagesFilter,
			isLoadingReviews,
			isLoadingMoreReviews,
			isLoadingReviewImages,
			isLoadingSelectedReviews,
			isReviewsPopoverOpen,
			loadMoreReviews,
			openReviewsPopover,
			resetReviewScore,
			reviewImages,
			reviews,
			reviewsHasNextPage,
			selectedReviews,
			selectedReviewScore,
			selectedReviewsSortOption,
			setReviewScore,
			setReviewsSortOption,
		],
	);

	return <ReviewContext value={value}>{children}</ReviewContext>;
}
ReviewContextProvider.displayName = 'ReviewContextProvider';

export function useReviewContext() {
	return useRequiredContext(ReviewContext, 'ReviewContext');
}
